import { Container, Graphics, Text } from 'pixi.js';
import { COLORS, DESIGN_W } from '../app/theme';
import { viewport } from '../app/viewport';
import { Button, makeText } from './widgets';
import { GameTray, TRAY_TITLE } from './gameTray';
import { Slider } from './Slider';
import { moneyFa } from '../util/persian';

export interface BetControlOpts {
  /** Current bet — the floor you can pick (server requires betValue >= current). */
  current: number;
  /** Raise increment = MinBet. */
  step: number;
  /** Ceiling = min(both balances) (server requires betValue <= that). */
  max: number;
  onConfirm: (amount: number) => void;
  onCancel: () => void;
}

/**
 * The raise picker — Unity's "میزان افزایش" tray that slides up from the bottom. Slider changes are
 * staged until confirmation; the quick buttons submit immediately (دوبرابر = current bet ×2,
 * کل بانک = all-in). The server validates the range again and, on success, auto-deals a card — so
 * the caller must NOT also send a Hit. Self-removes on submit/cancel.
 */
export class BetControl extends Container {
  private amount: number;
  private settled = false;
  private readonly amountText: Text;
  private readonly slider: Slider;
  private readonly tray: GameTray;

  constructor(private readonly opts: BetControlOpts) {
    super();
    this.amount = Math.max(opts.current, Math.min(opts.max, opts.current + opts.step));

    const dim = new Graphics().rect(0, 0, DESIGN_W, viewport.h).fill({ color: COLORS.scrim, alpha: 0.5 });
    dim.eventMode = 'static';
    dim.on('pointertap', () => this.cancel());
    this.addChild(dim);

    const trayW = DESIGN_W - 24;
    const trayH = 286;
    this.tray = new GameTray(trayW, trayH, 'bottom');
    this.addChild(this.tray);
    const c = this.tray.content;

    const title = makeText('میزان افزایش', { size: 28, weight: '700', align: 'center', fill: TRAY_TITLE });
    title.anchor.set(0.5, 0);
    title.position.set(trayW / 2, 20);
    c.addChild(title);

    const back = new Button('بازگشت', () => this.cancel(), { width: 130, height: 48, fontSize: 20 });
    back.position.set(trayW - 130 - 20, 18);
    c.addChild(back);

    this.amountText = makeText('', { size: 30, weight: '700', align: 'center', fill: TRAY_TITLE });
    this.amountText.anchor.set(0.5);
    this.amountText.position.set(trayW / 2, 96);
    c.addChild(this.amountText);

    // Quick-amount buttons.
    const dbl = new Button('دوبرابر', () => this.submitAmount(opts.current * 2), {
      width: 150,
      height: 54,
      fontSize: 22,
      fill: COLORS.panelSoft,
      textColor: COLORS.text,
    });
    dbl.position.set(24, 132);
    c.addChild(dbl);

    const allIn = new Button('کل بانک', () => this.submitAmount(opts.max), {
      width: 150,
      height: 54,
      fontSize: 22,
      fill: COLORS.panelSoft,
      textColor: COLORS.text,
    });
    allIn.position.set(trayW / 2 - 75, 132);
    c.addChild(allIn);

    const confirm = new Button('شرط‌بندی', () => this.submitAmount(this.amount), { width: 150, height: 54, fontSize: 22 });
    confirm.position.set(trayW - 150 - 24, 132);
    c.addChild(confirm);

    this.slider = new Slider({
      width: trayW - 80,
      min: opts.current,
      max: opts.max,
      step: Math.max(1, opts.step),
      value: this.amount,
      onChange: (v) => {
        this.amount = v;
        this.refresh();
      },
    });
    this.slider.position.set(40, 206);
    c.addChild(this.slider);

    this.refresh();
    this.tray.show();
  }

  private setAmount(v: number): void {
    this.amount = Math.max(this.opts.current, Math.min(this.opts.max, Math.round(v)));
    this.slider.setValue(this.amount, false);
    this.refresh();
  }

  private refresh(): void {
    this.amountText.text = `مقدار: ${moneyFa(this.amount)}`;
  }

  private submitAmount(value: number): void {
    if (this.destroyed || this.settled) return;
    this.setAmount(value);
    this.settled = true;
    this.opts.onConfirm(this.amount);
    this.close();
  }

  private cancel(): void {
    if (this.destroyed || this.settled) return;
    this.settled = true;
    this.opts.onCancel();
    this.close();
  }

  private close(): void {
    if (this.destroyed) return;
    // Slide the tray out, then drop the whole control (dim + tray).
    this.tray.hide(() => {
      if (!this.destroyed) this.destroy({ children: true });
    });
  }
}
